import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useKyc } from '../context/KycContext';
import { useAlert } from '../hooks/useAlert';
import { useToast } from '../context/ToastContext';
import { PageHeader } from '../components/Form';
import { ListRow, SectionHeader, StatusPill } from '../components/PlayerUI';
import LanguageSelector from '../components/LanguageSelector';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { SkeletonCard } from '../components/Skeleton';

const AVATAR_KEY = 'user_avatar';
const AVATARS = ['user', 'user-ninja', 'user-astronaut', 'robot', 'cat', 'dog', 'chess-king', 'gamepad', 'dice', 'bolt', 'rocket', 'star'];
type UserProfile = { fullName: string; username: string; email: string | null; phone: string | null };

export default function Profile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { colors, isDark, toggleTheme } = useTheme();
  const { verified, mustVerify, openKyc } = useKyc();
  const { alert } = useAlert();
  const toast = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [avatar, setAvatar] = useState(() => localStorage.getItem(AVATAR_KEY) || 'user');
  const [modal, setModal] = useState<'personal' | 'avatar' | 'language' | 'privacy' | null>(null);
  const [policy, setPolicy] = useState('');
  const [policyLoading, setPolicyLoading] = useState(false);

  useEffect(() => {
    api.get<UserProfile>('/auth/me').then(response => setProfile(response.data)).catch(() => null).finally(() => setLoading(false));
  }, []);

  const chooseAvatar = (icon: string) => {
    setAvatar(icon);
    try { localStorage.setItem(AVATAR_KEY, icon); } catch { /* Storage may be unavailable. */ }
    setModal(null);
  };

  const openPrivacy = async () => {
    setModal('privacy');
    if (policy) return;
    setPolicyLoading(true);
    try {
      const response = await api.get<{ content: string }>('/privacy-policy');
      setPolicy(response.data.content || t('profile.privacy.empty'));
    } catch { setPolicy(t('profile.privacy.failed')); }
    finally { setPolicyLoading(false); }
  };

  const signOut = () => alert(t('profile.signOut.confirmTitle'), t('profile.signOut.confirmMessage'), [
    { text: t('actions.cancel'), style: 'cancel' },
    { text: t('profile.signOut.label'), style: 'destructive', onPress: () => logout().catch(() => null) },
  ]);

  return (
    <main className="sls-page">
      <PageHeader title={t('profile.account.title')} right={<button aria-label={t('profile.openNotifications')} onClick={() => navigate('/notifications')} style={{ width: 48, height: 48, border: 0, borderRadius: 24, background: 'transparent' }}><Icon name="bell" size={19} color={colors.text} /></button>} />
      {loading ? <div className="sls-page-content"><SkeletonCard /><SkeletonCard /></div> : (
        <div className="sls-page-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '4px 0 12px' }}>
            <button aria-label={t('profile.changeAvatarAria')} onClick={() => setModal('avatar')} style={{ width: 72, height: 72, borderRadius: 36, border: 0, background: colors.accent, display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name={avatar} size={30} color={colors.textInverse} /></button>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{t('profile.account.greeting', { name: profile?.fullName?.split(' ')[0] || t('profile.defaultName') })}</h1>
              <p style={{ margin: '4px 0 0', color: colors.textSub, fontSize: 15 }}>@{profile?.username || '—'}</p>
              {verified ? <span style={{ display: 'inline-block', marginTop: 8 }}><StatusPill status="completed" label={t('profile.account.verified')} /></span> : mustVerify ? <button onClick={openKyc} style={{ minHeight: 44, marginTop: 6, padding: 0, border: 0, background: 'transparent', color: colors.text, fontSize: 14, fontWeight: 600 }}>{t('profile.account.verify')}</button> : null}
            </div>
          </div>

          <SectionHeader title={t('profile.account.money')} />
          <ListRow icon="user" label={t('profile.account.personal')} onPress={() => setModal('personal')} />
          <ListRow icon="receipt" label={t('profile.transactions.title')} onPress={() => navigate('/transactions')} />
          <ListRow icon="credit-card" label={t('profile.rowsPayout.label')} onPress={() => navigate('/payout-methods')} />

          <SectionHeader title={t('profile.account.support')} />
          <ListRow icon="bell" label={t('profile.account.notifications')} onPress={() => navigate('/notifications')} />
          <ListRow icon="sliders-h" label={t('profile.rows.notificationPrefs')} onPress={() => navigate('/notification-prefs')} />
          <ListRow icon="question-circle" label={t('profile.rows.faq')} onPress={() => navigate('/faq')} />
          <ListRow icon="book-open" label={t('profile.rows.howItWorks')} onPress={() => navigate('/help')} />
          <ListRow icon="headset" label={t('profile.account.contactSupport')} onPress={() => { window.location.href = `mailto:${t('auth.login.supportEmail')}`; }} />

          <SectionHeader title={t('profile.account.preferences')} />
          <ListRow icon="language" label={t('profile.language.label')} onPress={() => setModal('language')} />
          <ListRow icon={isDark ? 'moon' : 'sun'} label={t('profile.account.appearance')} value={isDark ? t('profile.guest.dark') : t('profile.guest.light')} onPress={toggleTheme} />
          <ListRow icon="shield-alt" label={t('profile.account.securityPrivacy')} onPress={openPrivacy} />
          <ListRow icon="gift" label={t('profile.account.bonuses')} onPress={() => navigate('/events')} />

          <Button label={t('profile.signOut.label')} variant="secondary" onClick={signOut} style={{ marginTop: 28 }} />
        </div>
      )}

      <Modal open={modal === 'personal'} onClose={() => setModal(null)}>
        <h2 style={{ margin: '0 0 18px', fontSize: 20 }}>{t('profile.account.personal')}</h2>
        <Info label={t('profile.account.fullName')} value={profile?.fullName || '—'} />
        <Info label={t('auth.login.username')} value={profile?.username || '—'} />
        <Info label={t('profile.account.email')} value={profile?.email || '—'} />
        <Info label={t('profile.account.phone')} value={profile?.phone || '—'} />
        <Button label={t('actions.close')} variant="secondary" onClick={() => setModal(null)} style={{ marginTop: 18 }} />
      </Modal>

      <Modal open={modal === 'avatar'} onClose={() => setModal(null)}>
        <h2 style={{ margin: '0 0 18px', fontSize: 20 }}>{t('profile.avatar.title')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>{AVATARS.map(icon => <button key={icon} aria-label={t('profile.avatar.select', { icon })} onClick={() => chooseAvatar(icon)} style={{ minHeight: 56, borderRadius: 12, border: `1px solid ${avatar === icon ? colors.accent : colors.line}`, background: avatar === icon ? colors.accentSoft : colors.surface }}><Icon name={icon} size={21} color={colors.text} /></button>)}</div>
      </Modal>

      <Modal open={modal === 'language'} onClose={() => setModal(null)}>
        <h2 style={{ margin: '0 0 18px', fontSize: 20 }}>{t('profile.language.label')}</h2>
        <LanguageSelector persistToServer onChanged={() => toast.success(t('language.changed'))} />
        <Button label={t('actions.close')} variant="secondary" onClick={() => setModal(null)} style={{ marginTop: 18 }} />
      </Modal>

      <Modal open={modal === 'privacy'} onClose={() => setModal(null)} contentStyle={{ maxHeight: '80%' }}>
        <h2 style={{ margin: '0 0 18px', fontSize: 20 }}>{t('profile.privacy.title')}</h2>
        {policyLoading ? <SkeletonCard /> : <div style={{ maxHeight: 420, overflowY: 'auto', color: colors.textSub, fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{policy}</div>}
        <Button label={t('actions.close')} variant="secondary" onClick={() => setModal(null)} style={{ marginTop: 18 }} />
      </Modal>
    </main>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  const { colors } = useTheme();
  return <div style={{ padding: '10px 0', borderBottom: `1px solid ${colors.line}` }}><div style={{ color: colors.textMuted, fontSize: 14 }}>{label}</div><div style={{ marginTop: 3, fontSize: 16, overflowWrap: 'anywhere' }}>{value}</div></div>;
}
